import { animateClose, animateOpen } from '@gbds/core/src/ts/utils'
import styles from '@scss/overlay/overlay.scss'
import { unlockBodyScroll } from '../utils/body-scroll-lock'

export type OverlayIndeces = 122 | 123 | 124 | 125 | 128

export class Overlay {
	protected _overlay: HTMLDivElement
	private _zIndex: OverlayIndeces = 122

	/**
	 * Determines if the overlay will be closed when it is clicked on.
	 */
	public closeOverlayOnClick = true

	/**
	 * @param root Root to attach the overlay to, defaults to document.body.
	 * @param prepend Determines if the root is prepended, defaults to false.
	 */
	constructor(root?: HTMLElement | Element | ShadowRoot, prepend = false) {
		if (root) {
			this._initialize(root, prepend)
		} else {
			window.addEventListener('DOMContentLoaded', () => this._initialize(document.body, prepend), { once: true })
		}
	}

	private _initialize(root: HTMLElement | Element | ShadowRoot, prepend: boolean) {
		const existingOverlay = root.querySelector('div.gb-overlay')
		if (!existingOverlay) {
			this._initializeOverlayElement(prepend, root)
		} else {
			console.warn('An `Overlay` component already exists in the light DOM of root: ', root)
		}
	}

	protected _initializeOverlayElement(prepend: boolean, root: HTMLElement | Element | ShadowRoot) {
		this._overlay = document.createElement('div')
		this._overlay.classList.add('gb-overlay')

		const style = document.createElement('style')
		style.textContent = styles.toString()

		this.setZHeight()
		this._insertStyles(prepend, root, style)
		this._addOverlayClickListener(this._overlay)
	}

	protected _insertStyles(prepend: boolean, root: HTMLElement | Element | ShadowRoot, styles: HTMLStyleElement) {
		prepend ? root.prepend(styles) : root.appendChild(styles)
		prepend ? root.insertBefore(this._overlay, styles.nextElementSibling) : root.appendChild(this._overlay)
	}

	private _openOverlay() {
		animateOpen(this._overlay)
	}

	protected _addOverlayClickListener(overlay: HTMLDivElement) {
		overlay.addEventListener('mousedown', e => {
			if (e.target === this._overlay && this.closeOverlayOnClick) {
				this.toggleOverlay()
				document.dispatchEvent(new CustomEvent('gb-overlayClosedFromClick'))
				e.preventDefault()
			}
		})
	}

	private _closeOverlay() {
		animateClose(this._overlay)
		unlockBodyScroll()
	}

	/**
	 * Run a callback when the overlay is finished animating.
	 * @param callback The function to execute when the overlay is finished animating.
	 */
	public addOverlayFinishAnimatingCallback(callback: () => void): void {
		this._overlay.addEventListener('animationend', () => callback(), { once: true })
	}

	/**
	 * Toggles the overlay visibility.
	 *
	 */
	public toggleOverlay(): void {
		this.overlayShown ? this._closeOverlay() : this._openOverlay()
	}

	/**
	 * Returns true if the overlay is visible.
	 */
	public get overlayShown(): boolean {
		return this._overlay.classList.contains('open')
	}

	/**
	 * Returns true if the overlay is actively animating.
	 */
	public get overlayAnimating(): boolean {
		return this._overlay.classList.contains('opening') || this._overlay.classList.contains('closing')
	}

	public get zHeight(): OverlayIndeces {
		return this._zIndex
	}

	/**
	 * Sets the z-index of the overlay. Defaults to 122.
	 *
	 * ***Important:*** the z-index is relative to the parent root element used upon construction of this class.
	 *
	 * ***Note:*** the global nav's z-index is 124.
	 *
	 * @param zIndex Determines if the overlay is displayed over the navigation or under it
	 * @returns The zIndex of the overlay.
	 */
	public setZHeight(zIndex: OverlayIndeces = 122): number {
		this._zIndex = zIndex
		this._overlay.style.zIndex = this._zIndex.toString()
		return this._zIndex
	}
}
